import { Skeleton } from '@affine/component';
import { useWorkspaceInfo } from '@affine/core/components/hooks/use-workspace-info';
import type { WorkspaceMetadata } from '@affine/core/modules/workspace';
import { DoneIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import type { HTMLAttributes } from 'react';
import { forwardRef } from 'react';

import { WorkspaceAvatar } from '../../workspace-avatar';
import { workspaceDisplayName } from './display-name';
import * as styles from './styles.css';

export const PureWorkspaceCard = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    workspaceMetadata: WorkspaceMetadata;
    avatarSize?: number;
    disable?: boolean;
    active?: boolean;
  }
>(
  (
    {
      workspaceMetadata,
      avatarSize = 32,
      className,
      disable,
      active,
      ...props
    },
    ref
  ) => {
    const information = useWorkspaceInfo(workspaceMetadata);

    const name = workspaceDisplayName(information);

    return (
      <div
        className={clsx(
          styles.container,
          disable ? styles.disable : null,
          className
        )}
        role="button"
        tabIndex={0}
        data-testid="workspace-card"
        ref={ref}
        {...props}
      >
        <div className={styles.infoContainer}>
          {information ? (
            <WorkspaceAvatar
              meta={workspaceMetadata}
              rounded={3}
              data-testid="workspace-avatar"
              size={avatarSize}
              name={name}
              colorfulFallback
            />
          ) : (
            <Skeleton width={avatarSize} height={avatarSize} />
          )}
          <div className={styles.workspaceTitleContainer}>
            {information ? (
              // Never render a blank label — a resolved profile without a name falls back to
              // "Untitled" (the same fallback the avatar uses).
              <span className={styles.workspaceName}>{name}</span>
            ) : (
              <Skeleton width={100} />
            )}
          </div>
        </div>
        {active && (
          <div className={styles.activeContainer}>
            <DoneIcon className={styles.activeIcon} />
          </div>
        )}
      </div>
    );
  }
);

PureWorkspaceCard.displayName = 'PureWorkspaceCard';
